import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Notification from "./Notification";

const defaultProps = {
  count: 3,
  notifications: [
    {
      id: 1,
      title: "New message received",
      description: "John Doe sent you a message",
      isRead: false,
      icon: "bi bi-chat-dots",
      created: "2026-02-18T10:07:12.336Z",
    },
    {
      id: 2,
      title: "Your report is ready",
      description: "Monthly sales report has been generated",
      isRead: false,
      icon: "bi bi-file-earmark-text",
      created: "2025-02-18T10:30:00.000Z",
    },
    {
      id: 3,
      title: "Server update complete",
      description: "The system update was successful",
      isRead: true,
      icon: "bi bi-check-circle",
      created: "2025-02-17T10:30:00.000Z",
    },
  ],
};

const MAX_VISIBLE = 5;

function NotificationMenu({ notifications = [], onClick, onViewAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unread = (notifications || []).filter((n) => !n.isRead);
  const displayed = unread.slice(0, MAX_VISIBLE);
  const remainingCount = unread.length - MAX_VISIBLE;
  const unreadCount = unread.length;
  const hasUnread = unreadCount > 0;

  const handleNotificationClick = (notif) => {
    if (notif?.route) {
      setOpen(false);
      navigate(notif.route);
    }
    onClick?.(notif);
  };

  const handleMenuAction = (notif, action) => {
    if (action === "open" && notif?.route) {
      setOpen(false);
      navigate(notif.route);
    }
  };

  return (
    <div className="position-relative" ref={ref}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        type="button"
        className="btn btn-sm btn-link fs-5 text-dark position-relative"
      >
        <i className="bi bi-bell"></i>
        {unreadCount > 0 && (
          <span
            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
            style={{ fontSize: "0.7rem" }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="card shadow position-absolute end-0 mt-1"
          style={{ width: "320px", zIndex: 1050 }}
        >
          <div className="card-header d-flex justify-content-between align-items-center py-2">
            <span className="fw-semibold">Notifications</span>
            {unreadCount > 0 && (
              <span className="badge bg-danger rounded-pill">
                {unreadCount} unread
              </span>
            )}
          </div>

          <ul
            className="list-group list-group-flush"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            {hasUnread ? (
              <>
                {displayed.map((notif) => {
                  const { id, title, description, isRead, created } = notif;
                  return (
                    <Notification
                      key={id}
                      title={title}
                      description={description}
                      isRead={isRead}
                      created={created}
                      {...notif}
                      onClick={() => handleNotificationClick(notif)}
                      onMenuAction={handleMenuAction}
                      notification={notif}
                    />
                  );
                })}
                {remainingCount > 0 && (
                  <li className="list-group-item text-center py-2 text-muted small">
                    {remainingCount} more unread notification
                    {remainingCount !== 1 ? "s" : ""}
                  </li>
                )}
              </>
            ) : (
              <li className="list-group-item d-flex flex-column align-items-center justify-content-center py-5 text-center">
                <div
                  className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center mb-3"
                  style={{ width: "56px", height: "56px" }}
                >
                  <i className="bi bi-bell-slash fs-4"></i>
                </div>
                <p className="mb-1 fw-semibold small">
                  No unread notifications
                </p>
                <p className="mb-0 text-muted" style={{ fontSize: "12px" }}>
                  You're all caught up! Check back later.
                </p>
              </li>
            )}
          </ul>

          <div className="card-footer text-center py-2">
            <p
              className="text-primary small text-decoration-none m-0 cursor-pointer"
              onClick={onViewAll}
            >
              View all notifications
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

NotificationMenu.defaultProps = defaultProps;
export default NotificationMenu;
